import { getAuth } from 'firebase/auth';

/**
 * Get Firebase auth token for API authentication
 */
async function getAuthToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
}

/**
 * Upload a document file via the backend endpoint.
 * The backend handles Storage upload (via service account) and OCR/GPT processing.
 *
 * @param {string} clientId - The client document ID
 * @param {string} collection - One of: afianzadoras, obligados_solidarios, bienes_inmuebles
 * @param {string} docId - Document ID in the subcollection
 * @param {string} fileType - The specific file slot (e.g. contratoMultiple, csf, escritura)
 * @param {File} file - The file to upload
 */
export async function uploadDocumentFile(clientId, collection, docId, fileType, file) {
  const token = await getAuthToken();

  const formData = new FormData();
  formData.append('file', file);
  formData.append('clientId', clientId);
  formData.append('collection', collection);
  formData.append('docId', docId);
  formData.append('fileType', fileType);

  const API_BASE = process.env.REACT_APP_API_URL;
  const response = await fetch(`${API_BASE}/upload-document-file`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json();
    switch (response.status) {
      case 400:
        throw new Error(data.error || 'Invalid request parameters');
      case 401:
        throw new Error(data.error || 'Authentication failed - invalid or expired token');
      case 403:
        throw new Error(data.error || 'Access denied - user does not have access to this resource');
      case 404:
        throw new Error(data.error || 'Resource not found');
      case 500:
        throw new Error(data.error || 'Error uploading file');
      default:
        throw new Error(data.error || 'Error uploading file');
    }
  }

  const data = await response.json();
  return data;
}

/**
 * Reprocess an already-uploaded document file.
 * Re-runs GPT/Gemini extraction without re-uploading the file.
 *
 * @param {string} clientId - The client document ID
 * @param {string} collection - One of: afianzadoras, obligados_solidarios, bienes_inmuebles
 * @param {string} docId - Document ID in the subcollection
 * @param {string} fileType - The specific file slot (e.g. contratoMultiple, csf, escritura)
 */
export async function reprocessDocumentFile(clientId, collection, docId, fileType) {
  const token = await getAuthToken();

  const API_BASE = process.env.REACT_APP_API_URL;
  const response = await fetch(`${API_BASE}/reprocess-document-file`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ clientId, collection, docId, fileType }),
  });

  if (!response.ok) {
    const data = await response.json();
    switch (response.status) {
      case 400:
        throw new Error(data.error || 'Invalid request parameters');
      case 401:
        throw new Error(data.error || 'Authentication failed - invalid or expired token');
      case 403:
        throw new Error(data.error || 'Access denied - user does not have access to this resource');
      case 404:
        throw new Error(data.error || 'Resource not found');
      case 429:
        throw new Error(data.error || 'Page limit exceeded');
      case 500:
        throw new Error(data.error || 'Error reprocessing file');
      default:
        throw new Error(data.error || 'Error reprocessing file');
    }
  }

  const data = await response.json();
  return data;
}
